import React, { useState } from 'react';
import { IconButton, Stack, Tooltip, Typography, Box, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  ContentCopy,
  ThumbUpOutlined,
  ThumbDownOutlined,
  FileDownload,
  TextSnippet,
  Code,
  Html,
  PictureAsPdf,
  Article
} from '@mui/icons-material';
import FeedbackDialog from './FeedbackDialog';
import { updateMessageReaction } from '@/api/messages';
import { copyRichText } from '@/utils/copyToClipboard';
import { exportToFile, ExportFormat, EXPORT_FORMATS } from '@/utils/exportToFile';
import { ThinkingMode } from '@/models/message';

interface MessageActionsProps {
  messageId: string;
  conversationId: string;
  text: string;
  reaction?: 'like' | 'dislike';
  modelName?: string;  // NEW: Use stored model name directly
  requestedMode?: ThinkingMode;
  effectiveMode?: ThinkingMode;
  onReactionChange?: () => void;
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  messageId,
  conversationId,
  text,
  reaction,
  modelName,
  requestedMode,
  effectiveMode,
  onReactionChange,
  onShowSnackbar
}) => {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative'>('positive');
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const handleCopy = async () => {
    await copyRichText(text);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = async (format: ExportFormat) => {
    handleExportClose();
    try {
      await exportToFile(text, format, {
        title: 'Bolt Response',
        filename: 'bolt-response'
      });
    } catch (error) {
      console.error('Export failed:', error);
      // For unavailable formats, show alert
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  };

  // Map format icons
  const getFormatIcon = (iconName: string) => {
    switch (iconName) {
      case 'TextSnippet': return <TextSnippet fontSize="small" />;
      case 'Code': return <Code fontSize="small" />;
      case 'Html': return <Html fontSize="small" />;
      case 'PictureAsPdf': return <PictureAsPdf fontSize="small" />;
      case 'Article': return <Article fontSize="small" />;
      default: return <TextSnippet fontSize="small" />;
    }
  };

  const handleReaction = (newReaction: 'like' | 'dislike') => {
    // Don't allow changing reaction once submitted
    if (reaction) {
      return;
    }
    
    setFeedbackType(newReaction === 'like' ? 'positive' : 'negative');
    setFeedbackDialogOpen(true);
  };

  const handleFeedbackSubmit = async (success: boolean, message: string) => {
    if (success) {
      // Update the reaction in the database
      await updateMessageReaction(conversationId, messageId, feedbackType === 'positive' ? 'like' : 'dislike');
      if (onReactionChange) {
        onReactionChange();
      }
    }
    
    // Show snackbar notification
    if (onShowSnackbar) {
      onShowSnackbar(message, success ? 'success' : 'error');
    }
  };

  const formatThinkingMode = (mode: ThinkingMode): string => {
    if (mode === 'deep') return 'Think deeper';
    if (mode === 'quick') return 'Quick';
    return 'Auto';
  };

  const modeText = (() => {
    if (!requestedMode && !effectiveMode) return undefined;

    const requested = requestedMode ?? effectiveMode;
    const effective = effectiveMode ?? requestedMode;
    if (!requested || !effective) return undefined;

    if (requested === 'auto' && effective !== 'auto') {
      return `Auto (${formatThinkingMode(effective)})`;
    }

    return formatThinkingMode(effective);
  })();

  const footerMetaText = [
    modelName,
    modeText ? `Mode: ${modeText}` : undefined,
  ].filter(Boolean).join(' • ');

  return (
    <>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Tooltip title="Copy response to clipboard" placement="bottom">
          <span>
            <IconButton 
              size="small" 
              onClick={handleCopy} 
              sx={{ 
                color: 'grey',
                padding: '4px'
              }}
            >
              <ContentCopy fontSize="small" sx={{ fontSize: '18px' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Export response to file" placement="bottom">
          <span>
            <IconButton 
              size="small" 
              onClick={handleExportClick}
              sx={{ 
                color: 'grey',
                padding: '4px'
              }}
            >
              <FileDownload fontSize="small" sx={{ fontSize: '18px' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={reaction ? "Feedback already submitted" : "Share positive feedback with Bolt Core Team"} placement="bottom">
          <span>
            <IconButton 
              size="small" 
              onClick={() => handleReaction('like')} 
              disabled={!!reaction}
              sx={{ 
                color: reaction === 'like' ? '#0079C2' : 'grey',
                padding: '4px',
                '&.Mui-disabled': {
                  color: reaction === 'like' ? '#0079C2' : 'grey'
                }
              }}
            >
              {reaction === 'like' ? (
                <ThumbUp fontSize="small" sx={{ fontSize: '18px' }} />
              ) : (
                <ThumbUpOutlined fontSize="small" sx={{ fontSize: '18px' }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={reaction ? "Feedback already submitted" : "Share negative feedback with Bolt Core Team"} placement="bottom">
          <span>
            <IconButton 
              size="small" 
              onClick={() => handleReaction('dislike')} 
              disabled={!!reaction}
              sx={{ 
                color: reaction === 'dislike' ? '#0079C2' : 'grey',
                padding: '4px',
                '&.Mui-disabled': {
                  color: reaction === 'dislike' ? '#0079C2' : 'grey'
                }
              }}
            >
              {reaction === 'dislike' ? (
                <ThumbDown fontSize="small" sx={{ fontSize: '18px' }} />
              ) : (
                <ThumbDownOutlined fontSize="small" sx={{ fontSize: '18px' }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        {footerMetaText && (
          <Box sx={{ ml: 1 }} data-model-name={modelName || ''}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontStyle: 'italic'
              }}
            >
              {footerMetaText}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 160,
            mt: 0.5
          }
        }}
      >
        {EXPORT_FORMATS.filter(f => f.available).map((format) => (
          <MenuItem 
            key={format.id} 
            onClick={() => handleExport(format.id)}
            sx={{ py: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getFormatIcon(format.icon)}
            </ListItemIcon>
            <ListItemText>{format.label}</ListItemText>
          </MenuItem>
        ))}
        
        {/* Divider and disabled formats */}
        {EXPORT_FORMATS.some(f => !f.available) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {EXPORT_FORMATS.filter(f => !f.available).map((format) => (
              <Tooltip 
                key={format.id} 
                title="Coming soon" 
                placement="right"
              >
                <span>
                  <MenuItem 
                    disabled
                    sx={{ py: 1, opacity: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getFormatIcon(format.icon)}
                    </ListItemIcon>
                    <ListItemText>{format.label}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            ))}
          </>
        )}
      </Menu>

      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        feedbackType={feedbackType}
        messageId={messageId}
        conversationId={conversationId}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};
